import React from 'react';
import * as Icons from 'lucide-react';
import { Star, BookOpen, TrendingUp } from 'lucide-react';
import { useApp } from '../context/AppContext';

export function CompanyIcon({ name, size = 20, logo }) {
  if (logo) {
    return <img src={logo} alt="" className="w-full h-full object-contain bg-white rounded-md p-0.5" />;
  }
  const Ico = Icons[name] || Icons.Building2;
  return <Ico size={size} className="text-[var(--orange)]" />;
}

export const fmtPrice = (n) => `$${n.toFixed(2)}`;
export const fmtVal = (n) => `$${n.toFixed(1)}B`;
export const fmtLot = (n) => `$${n.toLocaleString()}`;

export default function CompanyCard({ company, onOrderBook, onBuy }) {
  const { watchlist, toggleWatchlist } = useApp();
  const inWatch = watchlist.includes(company.id);
  const up = company.change >= 0;

  return (
    <div className="group rounded-xl bg-[var(--panel)] border border-[var(--border)] hover:border-[#3a3a42] transition-all duration-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/40 flex flex-col overflow-hidden">
      <div className="p-5 flex-1">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-[#1c1710] border border-[#3a2a18] flex items-center justify-center shrink-0">
              <CompanyIcon name={company.icon} />
            </div>
            <div>
              <h3 className="font-display font-semibold text-[15px] text-white leading-tight">{company.name}</h3>
              <div className="flex items-center gap-1.5 mt-1 text-[11px] font-mono">
                <span className="text-[var(--orange)] tracking-wide">{company.ticker}</span>
                <span className="text-[var(--muted-2)]">•</span>
                <span className="text-[var(--muted)]">{company.category}</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => toggleWatchlist(company.id)}
            className="text-[var(--muted-2)] hover:text-[var(--orange)] transition-colors p-1"
            aria-label="watchlist"
          >
            <Star size={17} className={inWatch ? 'fill-[var(--orange)] text-[var(--orange)]' : ''} />
          </button>
        </div>

        {/* Description */}
        <p className="text-[12.5px] text-[var(--muted)] mt-4 leading-relaxed line-clamp-2">
          {company.description}
        </p>

        {/* Prices */}
        <div className="grid grid-cols-2 gap-4 mt-5">
          <div>
            <div className="text-[9.5px] font-mono tracking-[0.12em] text-[var(--muted-2)]">LAST SHARE PRICE</div>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="font-mono font-semibold text-[19px] text-[var(--orange)]">{fmtPrice(company.lastPrice)}</span>
              <span className={`font-mono text-[11px] ${up ? 'text-[var(--green)]' : 'text-[var(--red)]'}`}>
                {up ? '+' : ''}{company.change.toFixed(2)}%
              </span>
            </div>
          </div>
          <div>
            <div className="text-[9.5px] font-mono tracking-[0.12em] text-[var(--muted-2)]">IMPLIED VALUATION</div>
            <div className="font-mono font-semibold text-[19px] text-[var(--salmon)] mt-1">{fmtVal(company.valuation)}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <div className="text-[9.5px] font-mono tracking-[0.12em] text-[var(--muted-2)]">BID / ASK</div>
            <div className="font-mono text-[13px] text-[var(--text)] mt-1">
              {fmtPrice(company.bid)} <span className="text-[var(--muted-2)]">/</span> {fmtPrice(company.ask)}
            </div>
          </div>
          <div>
            <div className="text-[9.5px] font-mono tracking-[0.12em] text-[var(--muted-2)]">MIN LOT SIZE</div>
            <div className="font-mono text-[13px] text-[var(--text)] mt-1">{fmtLot(company.minLot)}</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 border-t border-[var(--border)]">
        <button
          onClick={() => onOrderBook(company)}
          className="flex items-center justify-center gap-2 py-3 text-[12px] font-medium text-[var(--muted)] hover:text-white hover:bg-[var(--panel-2)] transition-colors border-r border-[var(--border)]"
        >
          <BookOpen size={14} /> VIEW ORDER BOOK
        </button>
        <button
          onClick={() => onBuy(company)}
          className="flex items-center justify-center gap-2 py-3 text-[12px] font-semibold text-[#052e20] bg-[var(--green)] hover:bg-[#13b878] transition-colors"
        >
          <TrendingUp size={14} /> BUY SHARES
        </button>
      </div>
    </div>
  );
}
